import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Radio, Trash2, ExternalLink, MapPin, User, RotateCcw, Archive, Square, Trash } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";

const TRASH_DAYS = 30;

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return "—"; }
}

function daysLeft(iso) {
  if (!iso) return TRASH_DAYS;
  try {
    const purgeAt = new Date(iso).getTime() + TRASH_DAYS * 86400000;
    return Math.max(0, Math.ceil((purgeAt - Date.now()) / 86400000));
  } catch (e) { return TRASH_DAYS; }
}

export default function AdminEvents() {
  const [filter, setFilter] = useState("active"); // active | ended | trash
  const [events, setEvents] = useState(null);
  const [toPurge, setToPurge] = useState(null);
  const [busy, setBusy] = useState(null); // token currently mutating

  const load = useCallback(() => {
    api.get(`/admin/events?status=${filter}`).then(({ data }) => setEvents(data || [])).catch(() => setEvents(false));
  }, [filter]);

  useEffect(() => {
    setEvents(null);
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const isEnded = filter === "ended";
  const isTrash = filter === "trash";

  const drop = (token) => setEvents((prev) => (Array.isArray(prev) ? prev.filter((e) => e.token !== token) : prev));

  const trashEvent = async (ev) => {
    setBusy(ev.token);
    try {
      await api.delete(`/admin/events/${ev.token}`);
      drop(ev.token);
      toast.success(`"${ev.event_name || "Event"}" moved to Trash — kept ${TRASH_DAYS} days`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Move to Trash failed");
    } finally { setBusy(null); }
  };

  const restore = async (ev) => {
    setBusy(ev.token);
    try {
      await api.post(`/admin/events/${ev.token}/restore`);
      drop(ev.token);
      toast.success(`"${ev.event_name || "Event"}" restored — the organizer can resume it`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
    } finally { setBusy(null); }
  };

  const endEvent = async (ev) => {
    setBusy(ev.token);
    try {
      await api.post(`/admin/events/${ev.token}/end`);
      drop(ev.token);
      toast.success(`"${ev.event_name || "Event"}" ended`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "End failed");
    } finally { setBusy(null); }
  };

  const untrash = async (ev) => {
    setBusy(ev.token);
    try {
      await api.post(`/admin/events/${ev.token}/untrash`);
      drop(ev.token);
      toast.success(`"${ev.event_name || "Event"}" restored from Trash`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
    } finally { setBusy(null); }
  };

  const confirmPurge = async () => {
    if (!toPurge) return;
    setBusy(toPurge.token);
    try {
      await api.delete(`/admin/events/${toPurge.token}/purge`);
      drop(toPurge.token);
      toast.success("Event permanently deleted");
      setToPurge(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(null); }
  };

  const Tab = ({ id, label, icon: Icon }) => (
    <button data-testid={`events-filter-${id}`} onClick={() => setFilter(id)}
      className={`rounded-full px-4 py-1.5 text-sm font-bold flex items-center gap-2 transition-all ${filter === id ? "btn-glow" : "glass hover:scale-105"}`}
      style={filter === id ? {} : { color: "var(--text-2)" }}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div data-testid="admin-events">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Tab id="active" label="Active / Live" icon={Radio} />
        <Tab id="ended" label="Ended / Archived" icon={Archive} />
        <Tab id="trash" label="Trash" icon={Trash} />
      </div>

      <div className="flex items-center gap-2 mb-4 text-sm" style={{ color: "var(--text-2)" }}>
        {filter === "active" && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
        <span>
          <span className="font-bold text-primary-c">{events === null || events === false ? "—" : events.length}</span>{" "}
          {isTrash ? "event(s) in Trash" : isEnded ? "ended event(s)" : "active / online event(s)"}
        </span>
        <span className="mono text-xs">· {isTrash ? `auto-purged after ${TRASH_DAYS} days` : isEnded ? "restore to let the organizer resume" : "auto-ends after 24h idle"} · refreshes every 8s</span>
      </div>

      {events === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>
      ) : events === false ? (
        <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load events.</p>
      ) : events.length === 0 ? (
        <div className="rounded-2xl glow-border p-10 text-center" style={{ background: "var(--surface)" }} data-testid="events-empty">
          {isTrash ? <Trash className="w-8 h-8 mx-auto text-primary-c opacity-60" /> : isEnded ? <Archive className="w-8 h-8 mx-auto text-primary-c opacity-60" /> : <Radio className="w-8 h-8 mx-auto text-primary-c opacity-60" />}
          <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>
            {isTrash ? `Trash is empty. Deleted events are kept here for ${TRASH_DAYS} days and can be restored.` : isEnded ? "No ended events. Sessions organizers finish (or that auto-end after 24h) will appear here to restore." : "No live events right now. Active organizer sessions will appear here."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-events-list">
          {events.map((ev) => (
            <div key={ev.token} data-testid={`admin-event-${ev.token}`} className="rounded-2xl glow-border p-5 flex flex-col" style={{ background: "var(--surface)" }}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-display font-bold truncate">{ev.event_name || "Open Play"}</div>
                {isTrash ? (
                  <span className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5 shrink-0 flex items-center gap-1" style={{ color: "#ef4444" }}>
                    <Trash className="w-3 h-3" /> Trashed
                  </span>
                ) : isEnded ? (
                  <span className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5 shrink-0 flex items-center gap-1" style={{ color: "var(--text-2)" }}>
                    <Archive className="w-3 h-3" /> {ev.auto_ended ? "Auto-ended" : "Ended"}
                  </span>
                ) : (
                  <span className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5 shrink-0 flex items-center gap-1 text-green-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Live
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs" style={{ color: "var(--text-2)" }}>
                <User className="w-3.5 h-3.5" /> <span className="truncate">{ev.organizer_name || ev.organizer_email}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-1 text-xs" style={{ color: "var(--text-2)" }}>
                {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.location}</span>}
                {ev.mode && <span className="capitalize">{ev.mode}</span>}
              </div>
              <div className="flex gap-4 mt-3 text-sm">
                <span className="mono"><span className="text-primary-c font-bold">{ev.matches ?? 0}</span> matches</span>
                <span className="mono"><span className="text-primary-c font-bold">{ev.players ?? 0}</span> players</span>
              </div>
              {isTrash ? (
                <div className="text-[10px] mono mt-2" style={{ color: "#eab308" }} data-testid={`admin-event-purge-in-${ev.token}`}>
                  Deleted {fmtWhen(ev.trashed_at)} · auto-purges in {daysLeft(ev.trashed_at)} day{daysLeft(ev.trashed_at) === 1 ? "" : "s"}
                </div>
              ) : (
                <div className="text-[10px] mono mt-2" style={{ color: "var(--text-2)" }}>Updated {fmtWhen(ev.updated_at)}</div>
              )}
              <div className="flex gap-2 mt-4">
                {isTrash ? (
                  <>
                    <button data-testid={`admin-event-untrash-${ev.token}`} onClick={() => untrash(ev)} disabled={busy === ev.token}
                      className="flex-1 btn-glow rounded-full py-2 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                      {busy === ev.token ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4" /> Restore</>}
                    </button>
                    <button data-testid={`admin-event-purge-${ev.token}`} onClick={() => setToPurge(ev)} aria-label="Delete permanently"
                      className="glass rounded-full w-10 flex items-center justify-center hover:scale-105 transition-transform">
                      <Trash2 className="w-4 h-4" style={{ color: "#ef4444" }} />
                    </button>
                  </>
                ) : (
                  <>
                    {isEnded ? (
                      <button data-testid={`admin-event-restore-${ev.token}`} onClick={() => restore(ev)} disabled={busy === ev.token}
                        className="flex-1 btn-glow rounded-full py-2 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy === ev.token ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4" /> Restore</>}
                      </button>
                    ) : (
                      <a href={`${window.location.origin}/live/${ev.token}`} target="_blank" rel="noreferrer"
                        data-testid={`admin-event-view-${ev.token}`}
                        className="flex-1 btn-glow rounded-full py-2 text-sm font-bold flex items-center justify-center gap-2">
                        <ExternalLink className="w-4 h-4" /> Live view
                      </a>
                    )}
                    {!isEnded && (
                      <button data-testid={`admin-event-end-${ev.token}`} onClick={() => endEvent(ev)} disabled={busy === ev.token}
                        className="glass rounded-full px-3 text-sm font-bold flex items-center justify-center gap-1.5 hover:scale-105 transition-transform disabled:opacity-50" style={{ color: "#eab308" }}>
                        {busy === ev.token ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Square className="w-3.5 h-3.5" /> End</>}
                      </button>
                    )}
                    <button data-testid={`admin-event-delete-${ev.token}`} onClick={() => trashEvent(ev)} disabled={busy === ev.token} aria-label="Move to Trash"
                      className="glass rounded-full w-10 flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50">
                      {busy === ev.token ? <Loader2 className="w-4 h-4 animate-spin text-primary-c" /> : <Trash2 className="w-4 h-4" style={{ color: "#ef4444" }} />}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!toPurge} onOpenChange={(o) => !o && setToPurge(null)}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="event-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2"><Trash2 className="w-5 h-5" style={{ color: "#ef4444" }} /> Delete permanently?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>
              This permanently deletes <span className="font-bold">{toPurge?.event_name || "this event"}</span> and its saved session. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="event-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="event-purge-confirm" onClick={(e) => { e.preventDefault(); confirmPurge(); }} disabled={busy === toPurge?.token}
              className="bg-red-600 hover:bg-red-700 text-white">
              {busy === toPurge?.token ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
